import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ShoppingBag, SunMoon } from 'lucide-react';
import { cn } from '../utils/cn';
import { getApiErrorMessage } from '../services/api';
import { telHref, useSite } from '../utils/site';
import { customerApi, useCustomerToken, type ProfileForm } from './customerApi';
import { AddressFields, EMPTY_ADDRESS, Field, MilkPlanPicker, addressErrors, inputCls, milkReady, milkSummary, toChoice, type MilkDraft } from './CustomerForms';

// New customers sign themselves up (added on request - no office step): the
// same details staff enter on the Customers form, a daily milk plan if they
// want one, and their own PIN. They are signed in at the end.

const STEPS = ['You', 'Address', 'Milk', 'PIN'] as const;

export default function SignupPage() {
  const site = useSite();
  const navigate = useNavigate();
  const signedIn = useCustomerToken();
  const info = useQuery({ queryKey: ['signup-info'], queryFn: customerApi.signupInfo });
  const [step, setStep] = useState(0);
  const [mobile, setMobile] = useState('');
  const [addr, setAddr] = useState<ProfileForm>(EMPTY_ADDRESS);
  const [wantsMilk, setWantsMilk] = useState<boolean | null>(null);
  const [milk, setMilk] = useState<MilkDraft | null>(null);
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { window.scrollTo(0, 0); setTried(false); }, [step]);
  if (signedIn && !busy) return <Navigate to="/shop/account" replace />;

  const i = info.data;
  const phone = i?.support_phone || site.phone;
  const callUs = phone ? <>Call us on <a className="font-semibold text-brand-green-700" href={telHref(phone)}>{phone}</a>.</> : null;
  if (info.isLoading) return <div className="h-80 animate-pulse rounded-xl bg-white" />;
  if (!i) return <p className="rounded-xl bg-white p-5 text-sm text-critical-600">{getApiErrorMessage(info.error, 'Could not load the sign-up form.')}</p>;
  if (!i.open) {
    return (
      <div className="flex flex-col gap-3 rounded-xl bg-white p-5">
        <h1 className="text-xl font-bold text-brand-green-900">New sign-ups are paused</h1>
        <p className="text-sm text-ink-600">We aren't taking new customers online right now. {callUs}</p>
        <Link to="/shop/account" className="text-sm font-semibold text-brand-green-700">Already a customer? Sign in</Link>
      </div>
    );
  }

  const m10 = mobile.replace(/\D/g, '').replace(/^(91|0)(?=[6-9]\d{9}$)/, '');
  const aErr = addressErrors(addr, i.pincodes);
  const errs: Record<number, string | null> = {
    0: addr.name.trim().length < 2 ? 'Enter your name' : !/^[6-9]\d{9}$/.test(m10) ? 'Enter your 10-digit mobile number'
      : addr.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr.email.trim()) ? 'Check the email ID, or leave it blank' : null,
    1: Object.keys(aErr).length ? 'Please fill in the highlighted boxes' : null,
    2: wantsMilk === null ? 'Choose daily milk, or just shop orders' : wantsMilk && !milk ? 'Pick a plan'
      : wantsMilk && !milkReady(milk, i) ? 'Choose how you would like to pay' : null,
    3: !/^\d{4,6}$/.test(pin) ? 'Your PIN must be 4 to 6 digits' : pin !== pin2 ? "The two PINs don't match" : null,
  };
  const next = () => { setTried(true); if (!errs[step]) setStep(step + 1); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (errs[3]) return;
    setBusy(true);
    setError(null);
    try {
      await customerApi.signup({
        ...addr, mobile: m10, pin,
        ...(wantsMilk && milk ? { subscription: toChoice(milk), billing_mode: milk.billing || undefined } : {}),
      });
      navigate('/shop/account', { replace: true, state: { welcome: true } });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create your account.'));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="flex items-center gap-2">
        {step > 0 ? (
          <button type="button" onClick={() => setStep(step - 1)} aria-label="Back" className="rounded-full p-1.5 text-brand-green-800"><ChevronLeft className="h-5 w-5" /></button>
        ) : <Link to="/shop/account" aria-label="Back to sign in" className="rounded-full p-1.5 text-brand-green-800"><ChevronLeft className="h-5 w-5" /></Link>}
        <h1 className="text-xl font-bold text-brand-green-900">Create your account</h1>
      </div>
      <ol className="grid grid-cols-4 gap-1.5">
        {STEPS.map((s, n) => (
          <li key={s} className="flex flex-col gap-1">
            <span className={cn('h-1.5 rounded-full', n <= step ? 'bg-brand-green-700' : 'bg-ink-300/40')} />
            <span className={cn('text-[11px] font-semibold', n === step ? 'text-brand-green-800' : 'text-ink-400')}>{n + 1}. {s}</span>
          </li>
        ))}
      </ol>

      <section className="flex flex-col gap-3 rounded-xl bg-white p-4">
        {step === 0 && (
          <>
            <Field label="Your name"><input className={inputCls} autoComplete="name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} /></Field>
            <Field label="Mobile number" hint="(this is your login)"><input className={inputCls} inputMode="numeric" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="98XXXXXXXX" /></Field>
            <Field label="Email ID" hint="(optional)"><input className={inputCls} type="email" autoComplete="email" value={addr.email} onChange={(e) => setAddr({ ...addr, email: e.target.value })} /></Field>
          </>
        )}
        {step === 1 && (
          <>
            <p className="text-sm text-ink-600">Where should we deliver?{i.delivery_area_note ? ` ${i.delivery_area_note}` : ''}</p>
            <AddressFields value={addr} onChange={setAddr} errors={tried ? aErr : {}} />
          </>
        )}
        {step === 2 && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {i.plans.length > 0 && (
                <button type="button" onClick={() => setWantsMilk(true)} className={cn('flex flex-col items-center gap-1 rounded-xl border p-3 text-sm font-semibold', wantsMilk === true ? 'border-brand-green-700 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/40 text-ink-600')}>
                  <SunMoon className="h-6 w-6" /> Daily milk at my door
                </button>
              )}
              <button type="button" onClick={() => setWantsMilk(false)} className={cn('flex flex-col items-center gap-1 rounded-xl border p-3 text-sm font-semibold', i.plans.length === 0 && 'col-span-2', wantsMilk === false ? 'border-brand-green-700 bg-brand-green-50 text-brand-green-800' : 'border-ink-300/40 text-ink-600')}>
                <ShoppingBag className="h-6 w-6" /> Just shop orders for now
              </button>
            </div>
            {wantsMilk && <MilkPlanPicker info={i} value={milk} onChange={setMilk} />}
            {wantsMilk === false && <p className="text-xs text-ink-500">You can start daily milk any time later from your account.</p>}
          </>
        )}
        {step === 3 && (
          <>
            <p className="text-sm text-ink-600">Choose a 4–6 digit PIN. You'll sign in with <b>{m10}</b> and this PIN — keep it to yourself.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="PIN"><input className={cn(inputCls, 'tracking-[0.4em]')} type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} /></Field>
              <Field label="PIN again"><input className={cn(inputCls, 'tracking-[0.4em]')} type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))} /></Field>
            </div>
            <dl className="flex flex-col gap-1.5 rounded-lg bg-surface-subtle p-3 text-sm">
              <div><dt className="inline font-semibold">{addr.name}</dt> <dd className="inline text-ink-500">· {m10}</dd></div>
              <dd className="text-ink-600">{[addr.house_number, addr.apartment_name, addr.address, addr.city, addr.pincode].filter((x) => x.trim()).join(', ')}</dd>
              <dd className="text-ink-600">{wantsMilk && milk ? `Daily milk: ${milkSummary(milk, i)}${milk.billing ? ` · ${milk.billing === 'Prepaid' ? 'Prepaid wallet' : 'Pay later'}` : ''}` : 'Shop orders only for now'}</dd>
            </dl>
          </>
        )}
        {tried && errs[step] && step !== 1 && <p className="text-sm font-medium text-critical-600" role="alert">{errs[step]}</p>}
        {error && <p className="text-sm font-medium text-critical-600" role="alert">{error}</p>}
      </section>

      {step < 3 ? (
        <button type="button" onClick={next} className="rounded-xl bg-brand-green-700 py-3 font-bold text-white">Next</button>
      ) : (
        <button type="submit" disabled={busy} className="rounded-xl bg-brand-green-700 py-3 font-bold text-white disabled:bg-ink-300">{busy ? 'Creating your account…' : 'Create my account'}</button>
      )}
      <p className="text-center text-sm text-ink-500">Already a customer? <Link to="/shop/account" className="font-semibold text-brand-green-700">Sign in</Link></p>
    </form>
  );
}
